/**
 * Pure transform functions for ImportData.
 *
 * Each function takes ImportData and returns a new ImportData with immutable
 * updates — new row/header objects are created, never mutated in place.
 *
 * Callers compose these via viewModel.updateImportData() which calls
 * setImportData once with the final result.
 */
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { ImportData, InternalColumns } from './importer-types'

// ---------------------------------------------------------------------------
// Row transforms (by company hash)
// ---------------------------------------------------------------------------

export function setMatchedChainByHash(
  data: ImportData,
  companyHash: string,
  chain: ChainSearchResult | null,
  manualMatch: boolean
): ImportData {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row[InternalColumns.COMPANY_HASH] === companyHash
        ? {
            ...row,
            [InternalColumns.MATCHED_CHAIN]: chain,
            [InternalColumns.IS_CHAIN_MANUAL_MATCH]: manualMatch,
          }
        : row
    ),
  }
}

export function setMatchedDoorByHash(
  data: ImportData,
  companyHash: string,
  door: DoorSearchResult | null,
  manualMatch: boolean
): ImportData {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row[InternalColumns.COMPANY_HASH] === companyHash
        ? {
            ...row,
            [InternalColumns.MATCHED_DOOR]: door,
            [InternalColumns.IS_DOOR_MANUAL_MATCH]: manualMatch,
          }
        : row
    ),
  }
}

export function setIsCustomCompanyByHash(
  data: ImportData,
  companyHashes: string[],
  isCustom: boolean
): ImportData {
  const hashSet = new Set(companyHashes)
  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(row[InternalColumns.COMPANY_HASH])
        ? { ...row, [InternalColumns.IS_CUSTOM_COMPANY]: isCustom }
        : row
    ),
  }
}

export function setCustomCompanyCategoryByHash(
  data: ImportData,
  companyHashes: string[],
  categoryId: number,
  subcategoryId: number | null
): ImportData {
  const hashSet = new Set(companyHashes)
  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(row[InternalColumns.COMPANY_HASH])
        ? {
            ...row,
            [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: categoryId,
            [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: subcategoryId,
            [InternalColumns.IS_CUSTOM_COMPANY]: true,
          }
        : row
    ),
  }
}

export function setReadyByHash(
  data: ImportData,
  companyHashes: string[],
  ready: boolean
): ImportData {
  const hashSet = new Set(companyHashes)
  return {
    ...data,
    rows: data.rows.map((row) =>
      hashSet.has(row[InternalColumns.COMPANY_HASH])
        ? { ...row, [InternalColumns.READY]: ready }
        : row
    ),
  }
}

// ---------------------------------------------------------------------------
// Row transforms (by row ID)
// ---------------------------------------------------------------------------

export function setMatchedChainByRowId(
  data: ImportData,
  rowId: string,
  chain: ChainSearchResult | null,
  manualMatch: boolean
): ImportData {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row[InternalColumns.ROW_ID] === rowId
        ? {
            ...row,
            [InternalColumns.MATCHED_CHAIN]: chain,
            [InternalColumns.IS_CHAIN_MANUAL_MATCH]: manualMatch,
          }
        : row
    ),
  }
}

export function setMatchedDoorByRowId(
  data: ImportData,
  rowId: string,
  door: DoorSearchResult | null,
  manualMatch: boolean
): ImportData {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row[InternalColumns.ROW_ID] === rowId
        ? {
            ...row,
            [InternalColumns.MATCHED_DOOR]: door,
            [InternalColumns.IS_DOOR_MANUAL_MATCH]: manualMatch,
          }
        : row
    ),
  }
}

export function setReadyByRowId(
  data: ImportData,
  rowIds: string[],
  ready: boolean
): ImportData {
  const idSet = new Set(rowIds)
  return {
    ...data,
    rows: data.rows.map((row) =>
      idSet.has(row[InternalColumns.ROW_ID])
        ? { ...row, [InternalColumns.READY]: ready }
        : row
    ),
  }
}

// ---------------------------------------------------------------------------
// Header transforms
// ---------------------------------------------------------------------------

export function setHeaderMapping(
  data: ImportData,
  originalHeader: string,
  mappedColumn: string
): ImportData {
  return {
    ...data,
    headers: data.headers.map((h) =>
      h.original === originalHeader ? { ...h, mapped: mappedColumn } : h
    ),
  }
}
